import { createTextEditorHandler, TextEditorHandler } from '@lessonup/pins-shared';
import { isRightMBClick } from '@lessonup/ui-components';
import { Editor } from '@tiptap/core';
import { useCallback, useEffect, useRef } from 'react';
import { DropZoneHandlerProps } from '../DropZonePinComponent.types';

export function useDropZoneHandlers(props: DropZoneHandlerProps) {
  const { active, isTextEditMode, setModeTextEdit } = props;
  const dropZonePinComponentRef = useRef<HTMLDivElement>(null);
  const dropZoneEditorHandlerRef = useRef<TextEditorHandler | null>(null);
  const registerEditor = useCallback((editor: Editor) => {
    dropZoneEditorHandlerRef.current = createTextEditorHandler(editor);
  }, []);
  const clickState = useRef({
    shouldTryForDetailMode: false,
    dragged: false,
  });
  const wasActive = useRef(false);

  useEffect(() => {
    if (active) {
      wasActive.current = true;
    } else {
      wasActive.current = false;
      clickState.current.dragged = false;
      clickState.current.shouldTryForDetailMode = false;
    }
  }, [active, isTextEditMode]);

  const handleTouchStart = useCallback(() => {
    // If the component wasn't active yet, we blur the editor, if it was active we blur it later if the user drags the component
    // This means that before dragging it's sometimes possible to type in the editor outside of detail mode, but right now we don't have a way to re-focus the editor on the cursor position
    if (active && !isTextEditMode) {
      clickState.current.shouldTryForDetailMode = true;
    } else {
      dropZoneEditorHandlerRef.current?.blur();
    }
  }, [active, isTextEditMode]);

  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      // If it's a right click we want to either:
      // - stop the contextMenu from appearing if in textEditMode
      // - return so the click doesn't count for textEditMode
      if (isRightMBClick(event)) {
        if (isTextEditMode) {
          event.preventDefault();
          event.stopPropagation();
        }

        return;
      }

      // If the component wasn't active yet, we blur the editor, if it was active we blur it later if the user drags the component
      // This means that before dragging it's sometimes possible to type in the editor outside of detail mode, but right now we don't have a way to re-focus the editor on the cursor position
      if (active && !isTextEditMode) {
        clickState.current.shouldTryForDetailMode = true;
      } else {
        dropZoneEditorHandlerRef.current?.blur();
      }
    },
    [active, isTextEditMode]
  );

  /**
   * When clicking outside of a text mark, but inside the PinComponent we want tiptap to put the focus cursor at the end.
   */
  const handleMouseUpTouchEnd = useCallback(
    (event: React.MouseEvent | React.TouchEvent) => {
      if (!active) return;

      if ((!isTextEditMode && !clickState.current.shouldTryForDetailMode) || clickState.current.dragged) {
        clickState.current.dragged = false;
        clickState.current.shouldTryForDetailMode = false;
        return;
      }

      clickState.current.shouldTryForDetailMode = false;

      setModeTextEdit?.(props.componentId);

      // Check if the click target is within the component (including nested divs) or on the tiptap root
      const target = event.target instanceof HTMLElement ? event.target : null;
      const onRoot = target && target.classList.contains('tiptap-root');
      const withinComponent = target && dropZonePinComponentRef.current?.contains(target);

      if (!isTextEditMode && (withinComponent || onRoot)) {
        dropZoneEditorHandlerRef.current?.focus.end();
      }
    },
    [active, isTextEditMode, setModeTextEdit, props.componentId]
  );

  const handleMouseMoveTouchMove = useCallback(() => {
    if (!active || !clickState.current.shouldTryForDetailMode) return;

    // If the user drags the component, we want to blur the editor and not go into detail mode
    clickState.current.dragged = true;
    dropZoneEditorHandlerRef.current?.blur();
  }, [active]);

  const handleOnContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (!isTextEditMode) return;
      event.stopPropagation();
    },
    [isTextEditMode]
  );

  return {
    registerEditor,
    dropZonePinComponentRef,
    handleMouseDown,
    handleTouchStart,
    handleMouseUpTouchEnd,
    handleMouseMoveTouchMove,
    handleOnContextMenu,
  };
}
